import videojs from 'video.js';
import structuredClone from '@ungap/structured-clone';
import utils from './utils.js';
import Moment from './moment.js';
import MomentsGaps from './moments-gaps.js';

const DEFAULTS = {
	url: '',
	types: [],
	interval: 60 //sec
};

/**
 * The class for managing moment list
 *
 * @param {Object} player   Main Player
 * @param {Object=} options Object of option names and values
 * @extends EventTarget
 */
class MomentsList extends videojs.EventTarget {
	constructor(player, options) {

		let options_ = Object.assign({}, DEFAULTS, options);

		super(player, options_);
		this.items = [];
		this.evStart = null;
		this.evFinish = options_.evFinish;
		this.timezone = options_.timezone;
		this.player = player;
		this.types = options_.types.slice();
		this.types_ = {};
		this.player_ready = false;
		this.timer = null;
		this.canShow = true;
		this.hideGaps = options_.hideGaps;
		this.finishTimeSendQueryQuotes = options.finishTimeSendQueryQuotes;

		this.options = options_;

		this.init();
	}

	init() {
		let ev = this.player.vast ? 'vast.contentPlay' : 'playing';
		this.player.one(ev, () => {
			this.player_ready = true;
		});

		utils.sendRequest(this.options.url).then((response) => {
			this.responseData = response;
			this.initTypes();
			this.initGaps();
			this.processMoments(response);
			this.setListenersForLive();
			this.player.setInterval(() => {	
				this.update();
			}, this.options.interval * 1000);
		});
	}

	dispose() {
		this.player.clearInterval(this.timer);
	}

	initTypes() {
		this.types.forEach((t) => {
			if (t.visible * 1) {
				this.types_[t.id] = t; 
			}
		});
		this.setSelectedTypes();
	}

	initGaps() {
		this.momentsGaps = new MomentsGaps(this.player, {
			gaps: this.getGaps()
		});
	}

	setSelectedTypes(types) {
		this.types.forEach((t) => { 
			t.selected = types ? types.includes(t.id) : t.show * 1; 
		});
	}

	show() {
		this.clear();
		this.momentsGaps.updateGapList(this.getGaps());
		let quotes = this.responseData.quotes;
		quotes = this.filterVisibleTypes(quotes);
		quotes = this.momentsGaps.changeMomentsBoundaries(quotes);
		this.sort(quotes).forEach((el, i) => {
			let item = new Moment(this.player, {
				id: el.id  * 1,
				description: el.description,
				start: el.start * 1,
				finish: el.finish * 1,
				typeId: el.quote_type_id * 1,
				listPos: i,
				list: this
			});
			this.items.push(item);
		});

		this.setEvStart(this.render.bind(this));
	}

	showByTypes(types) {
		this.setSelectedTypes(types);
		this.show();
	}

	sort(elems) {
		return elems.sort((a, b) => {
			return a.start - b.start;
		});
	}

	filterVisibleTypes(elems) {
		return elems.filter((el) => {
			return this.isVisibleType(el.quote_type_id * 1);
		});
	}

	isVisibleType(id) {
		return (this.types_[id] || {}).selected;
	}

	isEmptyData() {
		let elems = this.responseData && this.responseData.quotes || [];
		elems = Array.isArray(elems) ? elems : [elems];
		return elems.length;
	}

	setEvStart(cb) {
		if (this.timer) {
			this.player.clearInterval(this.timer);
			this.timer = null;
		}

		if (this.evStart) {
			cb && cb();
			return;
		}

		let check_times = 50;
		this.timer = this.player.setInterval(() => {
			if (!this.timer) return;
			if (this.evStart || !check_times || check_times <= 0) {
				this.player.clearInterval(this.timer);
				this.timer = null;
				return cb && cb();
			}
			check_times--;
			if (!this.player_ready) return;
			if (!this.player.duration()) return;

			try {
				let handler = this.player.tech_.sourceHandler_;
				this.evStart = handler && handler.playlists && handler.playlists.media() && 
				handler.playlists.media().dateTimeObject / 1000 || 
				this.player.hls_.levels[this.player.hls_.currentLevel].details.fragments[0].programDateTime / 1000 ||
				this.options.evStart;
			} catch (err) { }
		}, 100);
	}

	processMoments(response) {
		this.responseData = response;
		let showMoments = () => {
			this.canShow = false;
			this.show();
			this.trigger('updated');
			this.canShow = true;
		};
		if (this.canShow) {
			showMoments();
		} else {
			this.on('updated_live', showMoments);
		}
	}

	update() {
		const currentTime = Date.now() / 1000;

		if (this.finishTimeSendQueryQuotes <= currentTime) {
			return;
		}

		utils.sendRequest(this.options.url).then((resp) => {
			this.processMoments(resp);
		});
	}

	render() {
		this.items.forEach((item) => {
			item.render();
		});
	}

	clear() {
		this.items.forEach((item) => {
			item.clear();
		});
		this.items = []; 
	}

	setListenersForLive() {
		let previousDuration = this.player.duration();
		let callback = () => {
			if (!this.player.hls_ || !this.canShow) return;
			let hlsLevel = this.player.hls_.levels && this.player.hls_.levels[this.player.hls_.currentLevel];
			let isLive = hlsLevel && hlsLevel.details && hlsLevel.details.live;
			if (isLive && previousDuration != this.player.duration() && this.canShow) {
				this.canShow = false;
				this.show();
				this.canShow = true;
				previousDuration = this.player.duration();
				this.trigger('updated');
				this.trigger('updated_live');
			}
		};
		this.player.on('timeupdate', callback);
		this.player.on('durationchange', callback);
	}

	getGaps() {
		// В некоторых случаях (когда используется плеер редактора), есть необходимость не учитывать
		// пропуски
		return this.hideGaps ? [] : this.responseData && this.responseData.gaps;
	}
}

export default MomentsList;